<template>
  <div class="quick-search">
    <ys-text type="title5" class="title" data-test="quick-search-title">
      {{ title }}
    </ys-text>
    <ys-chip-group orientation="vertical">
      <ys-toggleable-chip
        v-for="option in options"
        :key="option.slug"
        ref="chipRefs"
        :label="option.label"
        :value="option.slug"
        name="quick-search"
        single-select
      >
        <x-icon
          slot="icon"
          :icon="option.icon"
          width="16px"
          height="16px"
          color="var(--ys-color-text-primary)"
        />
      </ys-toggleable-chip>
    </ys-chip-group>
  </div>
</template>

<script setup lang="ts">
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-chip-group'
import '@onxmaps/yellowstone-ui/ys-toggleable-chip'
import type { YsToggleableChip } from '@onxmaps/yellowstone-ui/ys-toggleable-chip'
import xIcon from '@/components/shared/x-icon/index.vue'
import type { QuickSearchOption } from '@/lib/quick-search/types'
import { onMounted, ref } from 'vue'

interface QuickSearchProps {
  title?: string
  options: QuickSearchOption[]
}
const { title = 'Explore Trails', options } = defineProps<QuickSearchProps>()

const emit = defineEmits<{
  (e: 'option-clicked', option: string): void
}>()

const chipRefs = ref<YsToggleableChip[]>([])

const handleItemClick = (slug: string) => {
  emit('option-clicked', slug)
}

onMounted(() => {
  // Hack for capturing click events on ys-toggleable-chip, and performing custom logic.
  // This is because the ys-toggleable-chip component does not emit a click event when clicked,
  // only a change event. But in the design for this component, the chips are meant to act as buttons.
  // This usage was approved by the YS design team, and technical approach suggested by the YS web dev team.
  chipRefs.value.forEach((chipRef) => {
    chipRef.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
        const value = chipRef.value as string
        handleItemClick(value)
      },
      { capture: true }
    )
  })
})
</script>

<style scoped lang="scss">
.quick-search {
  padding: var(--ys-spacing-200) var(--ys-spacing-250) 0;
}

.title {
  padding: var(--ys-spacing-100) 0;
}
</style>
